import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowDownToLine, ArrowUpFromLine, PauseCircle, AlertTriangle,
  Check, BadgeCheck, Layers
} from 'lucide-react';

const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Chrome = ({ title, right, children, bodyClass = '' }) => (
  <div className="showcase-mockup" aria-hidden="true">
    <div className="hm-window">
      <div className="hm-titlebar">
        <span className="hm-dots"><i /><i /><i /></span>
        <span className="hm-title">{title}</span>
        {right}
      </div>
      <div className={`hm-body ${bodyClass}`}>{children}</div>
    </div>
  </div>
);

/* ---------- 1. Stock ledger & alerts ---------- */
const MOVES = [
  { item: 'Cotton Yarn 30s', type: 'Inward', qty: '+480 kg' },
  { item: 'Polyol Resin', type: 'Outward', qty: '−120 kg' },
  { item: 'Mango Pulp', type: 'Inward', qty: '+60 cases' },
  { item: 'Dye Lot A-12', type: 'Hold', qty: '60 kg' },
  { item: 'Woven Fabric', type: 'Outward', qty: '−200 m' },
  { item: 'Paracetamol API', type: 'Inward', qty: '+900 kg' }
];
const TYPE_META = {
  Inward: { icon: ArrowDownToLine, cls: 'in' },
  Outward: { icon: ArrowUpFromLine, cls: 'out' },
  Hold: { icon: PauseCircle, cls: 'hold' }
};

export function StockLedgerMock() {
  const [rows, setRows] = useState(() => [0, 1, 3, 2].map((i) => ({ ...MOVES[i], id: i })));
  const nextRef = useRef(4);
  const idRef = useRef(100);
  useEffect(() => {
    if (reduced()) return undefined;
    const t = setInterval(() => {
      const m = MOVES[nextRef.current % MOVES.length];
      nextRef.current += 1;
      setRows((prev) => [{ ...m, id: idRef.current++, fresh: true }, ...prev].slice(0, 5));
    }, 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <Chrome
      title="Stock ledger"
      right={<span className="sl-total">12,480 kg on hand</span>}
      bodyClass="sl-body"
    >
      <div className="sl-alert">
        <AlertTriangle size={13} /> Binder 99% — 18 kg left, below reorder
      </div>
      <div className="sl-rows">
        {rows.map((r) => {
          const meta = TYPE_META[r.type] || TYPE_META.Hold;
          const Icon = meta.icon;
          return (
            <div key={r.id} className={`sl-row${r.fresh ? ' sl-fresh' : ''}`}>
              <span className={`sl-badge sl-${meta.cls}`}><Icon size={12} /></span>
              <span className="sl-item">{r.item}</span>
              <span className="sl-type">{r.type}</span>
              <span className={`sl-qty sl-${meta.cls}`}>{r.qty}</span>
            </div>
          );
        })}
      </div>
    </Chrome>
  );
}

/* ---------- 2. Quality checks & certificate ---------- */
const QC_CHECKS = [
  'Dimensions — within spec',
  'Density — 32 kg/m³',
  'Hardness (ILD) — pass',
  'Visual inspection — pass'
];

export function QualityCertMock() {
  return (
    <Chrome title="Quality control · Order #1042" bodyClass="qc-body">
      <div className="qc-checks">
        {QC_CHECKS.map((c, i) => (
          <div className="qc-check" key={c} style={{ '--i': i }}>
            <span className="qc-box"><Check size={12} /></span>
            <span className="qc-label">{c}</span>
          </div>
        ))}
      </div>
      <div className="qc-cert">
        <BadgeCheck size={22} />
        <div className="qc-cert-text">
          <strong>Test Certificate</strong>
          <span>Order #1042 · PU Foam Block</span>
        </div>
        <span className="qc-stamp">PASSED</span>
      </div>
    </Chrome>
  );
}

/* ---------- 3. Materials & BOM ---------- */
const BOM_LINES = [
  { name: 'Polyol Resin', char: 'base', qty: '60 kg' },
  { name: 'Isocyanate (TDI)', char: '—', qty: '42 kg' },
  { name: 'Water', char: 'blowing agent', qty: '2.4 kg' },
  { name: 'Silicone Surfactant', char: '1.2% recipe', qty: 'recipe' }
];

export function BomBuilderMock() {
  return (
    <Chrome title="Bill of materials · PU Foam Block" bodyClass="bom-body">
      <div className="bom-lines">
        {BOM_LINES.map((l, i) => (
          <div className="bom-line" key={l.name} style={{ '--i': i }}>
            <span className="bom-dot"><Layers size={12} /></span>
            <span className="bom-name">{l.name}</span>
            <span className="bom-char">{l.char}</span>
            <span className="bom-qty">{l.qty}</span>
          </div>
        ))}
      </div>
      <div className="bom-result">= <strong>PU Foam Block</strong> · 1 block finished</div>
    </Chrome>
  );
}
